import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { publicAPI } from '../services/api';

const Landing = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [appDownloadUrl, setAppDownloadUrl] = useState(process.env.REACT_APP_ANDROID_APP_URL || '#');
  const [googlePlayUrl, setGooglePlayUrl] = useState('');
  const [directApkUrl, setDirectApkUrl] = useState('');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadStep, setDownloadStep] = useState<'idle' | 'preparing' | 'resolving' | 'starting' | 'done' | 'failed'>('idle');
  const [downloadMessage, setDownloadMessage] = useState('');
  const timersRef = useRef<number[]>([]);

  useEffect(() => {
    const loadAppDownloadLink = async () => {
      try {
        const res = await publicAPI.getMaintenanceStatus();
        const appSettings = res?.data?.data?.app_settings || {};
        const resolvedDirect = appSettings.direct_apk_link || appSettings.latest_apk_url || '';
        const resolvedPlay = appSettings.google_play_link || '';
        setDirectApkUrl(resolvedDirect);
        setGooglePlayUrl(resolvedPlay);
        const dynamicUrl =
          resolvedDirect ||
          resolvedPlay ||
          '';
        if (dynamicUrl && typeof dynamicUrl === 'string') {
          setAppDownloadUrl(dynamicUrl);
        }
      } catch (e) {
        // Keep fallback env URL when API is unavailable
      }
    };
    loadAppDownloadLink();
  }, []);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
    };
  }, []);

  const clearDownloadTimers = () => {
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];
  };

  const openDownloadModal = () => {
    clearDownloadTimers();
    setDownloadStep('idle');
    setDownloadMessage('Choose download option below.');
    setShowDownloadModal(true);
  };

  const startDownloadFlow = (targetUrl: string, sourceLabel: string, openInNewTab: boolean) => {
    clearDownloadTimers();
    const url = (targetUrl || '').trim();
    if (!url || url === '#') {
      setDownloadStep('failed');
      setDownloadMessage(`${sourceLabel} link is not configured yet. Please contact support.`);
      return;
    }

    let popup: Window | null = null;
    if (openInNewTab) {
      popup = window.open('', '_blank');
      if (!popup) {
        setDownloadStep('failed');
        setDownloadMessage('Popup blocked by browser. Please allow popups and try again.');
        return;
      }
    }

    setDownloadStep('preparing');
    setDownloadMessage(`Preparing ${sourceLabel} request...`);

    timersRef.current.push(window.setTimeout(() => {
      setDownloadStep('resolving');
      setDownloadMessage('Verifying latest app link...');
    }, 500));

    timersRef.current.push(window.setTimeout(() => {
      setDownloadStep('starting');
      setDownloadMessage(`Starting ${sourceLabel}...`);
      if (openInNewTab && popup) {
        popup.location.href = url;
      } else {
        window.location.href = url;
      }
    }, 1200));

    timersRef.current.push(window.setTimeout(() => {
      setDownloadStep('done');
      setDownloadMessage('Download started. If it did not begin, check popup/download settings.');
    }, 1900));
  };

  if (!isLoading && isAuthenticated) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen w-full">
      <div id="landing-layout" className="min-h-screen w-full grid grid-cols-1 md:grid-cols-2 md:min-w-0">
        {/* Left: image block - hidden on mobile, visible on tablet/desktop */}
        <div className="landing-left flex max-md:hidden relative min-h-screen h-screen min-w-0 bg-slate-950 p-0 overflow-hidden order-2 md:order-1">
          <img
            src="/landing-side-img.jpg"
            alt="YuvanAPI Landing"
            className="w-full h-full object-cover object-center opacity-80"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-4">
            <div className="text-center rounded-2xl bg-black/40 backdrop-blur-sm px-5 py-4 shadow-xl border border-white/10 w-fit max-w-[min(100%,22rem)] mx-auto">
              <h1 className="text-white font-bold text-2xl sm:text-3xl md:text-4xl uppercase tracking-wider mb-3 m-0 drop-shadow">Download APP</h1>
              <button
                type="button"
                onClick={openDownloadModal}
                className="p-0 border-0 bg-transparent rounded-lg overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 transition inline-flex justify-center w-fit max-w-full mx-auto"
                aria-label="Download YuvanAPI Android App"
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/apk-download.png`}
                  alt=""
                  className="block w-auto h-auto max-w-full object-contain mx-auto pointer-events-none select-none"
                  style={{ maxHeight: 84, maxWidth: 340 }}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Right: panel block - full width on mobile, 50% on desktop */}
        <div className="landing-right relative min-h-screen flex flex-col min-w-0 bg-slate-900 px-4 py-8 sm:px-6 sm:py-10 md:px-12 md:py-16 order-1 md:order-2">
        <div
          className="absolute inset-0 z-0 pointer-events-none opacity-50"
          style={{
            backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.04) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
          aria-hidden
        />
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-[400px] mx-auto text-center md:text-left px-1 min-h-0">
          <div className="flex justify-center md:justify-start mb-2">
            <img
              id="landing-logo"
              src={`${process.env.PUBLIC_URL || ''}/logo.png`}
              alt="YuvanAPI Logo"
              className="w-52 h-auto sm:w-60 md:w-72 object-contain drop-shadow-lg shrink-0"
            />
          </div>
          <p className="text-xs sm:text-[0.8125rem] font-semibold tracking-[0.14em] uppercase text-violet-200 mb-3">
            Engineering Future Solutions
          </p>
          <h1 className="text-lg sm:text-xl font-bold tracking-tight text-white mb-3 leading-tight">
            YuvanAPI System v2.0
          </h1>
          <p className="text-base text-white/75 leading-relaxed mb-8">
            Choose the portal you want to access.
          </p>

          <div className="space-y-3 flex flex-col w-full">
            <Link
              to="/login"
              className="w-full py-3.5 px-6 text-sm sm:text-base font-semibold tracking-wide uppercase text-white rounded-lg bg-violet-600 hover:bg-violet-500 border border-violet-500/60 shadow-sm transition-colors text-center inline-block box-border"
            >
              Retail Login
            </Link>
          </div>

          {/* Mobile download CTA (shown below buttons) */}
          <div id="landing-mobile-download" className="mt-10 flex flex-col items-center space-y-3 md:hidden">
            <h2 className="text-white font-bold text-2xl uppercase tracking-wider mt-4 mb-1">
              Download APP
            </h2>
            <button
              type="button"
              onClick={openDownloadModal}
              className="p-0 border-0 bg-transparent rounded-lg overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 transition inline-flex justify-center w-fit max-w-full mx-auto"
              aria-label="Download YuvanAPI Android App"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/apk-download.png`}
                alt=""
                className="block w-auto h-auto max-w-full object-contain mx-auto pointer-events-none select-none"
                style={{ maxHeight: 84, maxWidth: 340 }}
              />
            </button>
          </div>
        </div>

        <div className="relative z-10 w-full max-w-[400px] mx-auto mt-auto pt-6 pb-8 sm:pb-6 shrink-0 text-center">
          <div className="flex flex-wrap items-end justify-center gap-3 mb-3">
            <img
              src="/specs.png"
              alt="SSL Secure Connection"
              className="h-30 w-auto object-contain object-bottom"
            />
           
          </div>
          <p className="text-sm text-slate-400 mt-2 mb-0">
            Built by Algobasket
          </p>
        </div>
        </div>
      </div>

      {showDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-violet-400/40 bg-slate-900/95 shadow-2xl shadow-violet-900/40 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-lg font-bold uppercase tracking-wide">Download App</h3>
              <button
                type="button"
                onClick={() => { clearDownloadTimers(); setShowDownloadModal(false); }}
                className="text-violet-200 hover:text-white text-xl leading-none"
                aria-label="Close download modal"
              >
                ×
              </button>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-xl border border-violet-500/30 bg-violet-500/10 mb-4">
              <span className={`w-3 h-3 rounded-full ${downloadStep === 'failed' ? 'bg-red-400' : 'bg-violet-400 animate-pulse'}`} />
              <p className="text-sm text-violet-100 m-0">{downloadMessage || 'Please wait...'}</p>
            </div>

            <div className="space-y-2 mb-4">
              <div className={`text-sm ${downloadStep === 'preparing' || downloadStep === 'resolving' || downloadStep === 'starting' || downloadStep === 'done' ? 'text-emerald-300' : 'text-slate-400'}`}>1. Preparing download request</div>
              <div className={`text-sm ${downloadStep === 'resolving' || downloadStep === 'starting' || downloadStep === 'done' ? 'text-emerald-300' : 'text-slate-400'}`}>2. Verifying latest app link</div>
              <div className={`text-sm ${downloadStep === 'starting' || downloadStep === 'done' ? 'text-emerald-300' : 'text-slate-400'}`}>3. Starting download</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 justify-items-center">
              <button
                type="button"
                onClick={() => startDownloadFlow(googlePlayUrl, 'Google Play download', true)}
                className="inline-flex p-0 border-0 bg-transparent rounded-xl overflow-hidden ring-1 ring-white/15 hover:ring-violet-400/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed cursor-pointer transition w-fit max-w-full"
                disabled={downloadStep === 'preparing' || downloadStep === 'resolving' || downloadStep === 'starting'}
                aria-label="Download from Google Play"
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/googleplay.png`}
                  alt=""
                  className="block w-auto max-w-[min(100%,10.5rem)] max-h-10 sm:max-h-11 h-auto object-contain mx-auto pointer-events-none select-none"
                />
              </button>
              <button
                type="button"
                onClick={() => startDownloadFlow(directApkUrl || appDownloadUrl, 'Direct APK download', false)}
                className="inline-flex p-0 border-0 bg-transparent rounded-xl overflow-hidden ring-1 ring-white/15 hover:ring-violet-400/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed cursor-pointer transition w-fit max-w-full"
                disabled={downloadStep === 'preparing' || downloadStep === 'resolving' || downloadStep === 'starting'}
                aria-label="Direct APK download"
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/apk.png`}
                  alt=""
                  className="block w-auto max-w-[min(100%,10.5rem)] max-h-10 sm:max-h-11 h-auto object-contain mx-auto pointer-events-none select-none"
                />
              </button>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { clearDownloadTimers(); setShowDownloadModal(false); }}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold"
              >
                {downloadStep === 'done' ? 'Done' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
